import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { AffiliateCodeLabelRequest, AffiliateCodeView } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';

const FIELDS = ['label'] as const;

export function EditCodeLabelDialog({
  code,
  onSave,
  onClose,
}: {
  code: AffiliateCodeView;
  onSave: (id: number, body: AffiliateCodeLabelRequest) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const describe = useActionError();
  const labelRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState(code.label ?? '');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { label: labelRef }, busy);
  async function save() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    fields.reset();
    try {
      // A blank label is the contract's clear: `null` (TMMAPI-0167).
      const trimmed = label.trim();
      await onSave(code.id, { label: trimmed.length > 0 ? trimmed : null });
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { label: 'label' }), {
        label: t('affiliates.field.label'),
      });
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <DialogTitle>{t('affiliates.code.editLabel', { code: code.code })}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} />
          <TextField
            fullWidth
            margin="dense"
            label={t('affiliates.code.labelField')}
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
              fields.clear('label');
            }}
            inputRef={labelRef}
            error={fields.errors.label !== undefined}
            helperText={fields.errors.label ?? t('affiliates.code.labelClearHint')}
            slotProps={{ htmlInput: { maxLength: 80, readOnly: busy } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={busy}>
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
